import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { NodeViewProps } from '@tiptap/react';
import type { MutableRefObject } from 'react';

import { Alert, Box, Heading, Text } from '@chakra-ui/react';
import { Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Suspense } from 'react';

import type { FilesBlockData } from '#pages/document/editorjs-types';

import {
  NP_FILES_TAG,
  normalizeFilesBlockData,
  parseMarkdownFilesConfig,
  serializeMarkdownFilesBlock,
  serializeMarkdownFilesInnerConfig,
} from './markdown-files';
import { DocumentFilesBlock } from './tools/files/document-files-block';

interface MarkdownFilesNodeOptions {
  contextRef: MutableRefObject<MarkdownEditorContextValue>;
}

interface MarkdownFilesNodeAttrs {
  data: FilesBlockData;
  rawConfig: string;
  parseError: string | null;
}

function normalizeFilesAttrs(attrs: unknown): MarkdownFilesNodeAttrs {
  const rec =
    attrs && typeof attrs === 'object'
      ? (attrs as Record<string, unknown>)
      : {};
  return {
    data: normalizeFilesBlockData(rec.data),
    rawConfig: typeof rec.rawConfig === 'string' ? rec.rawConfig : '',
    parseError: typeof rec.parseError === 'string' ? rec.parseError : null,
  };
}

function MarkdownFilesNodeView({ extension, selected, node }: NodeViewProps) {
  void node;
  const { contextRef } = extension.options as MarkdownFilesNodeOptions;
  const context = contextRef.current;
  const attrs = normalizeFilesAttrs(node.attrs);

  return (
    <NodeViewWrapper
      className={
        selected
          ? 'markdown-files-node markdown-files-node--selected'
          : 'markdown-files-node'
      }
      contentEditable={false}
      data-drag-handle={false}
    >
      <Box mb={attrs.parseError ? 3 : 0}>
        <Heading as="h2" size="sm">
          Files
        </Heading>
        <Text fontSize="sm" color="fg.muted">
          Attached uploads linked to this document.
        </Text>
      </Box>

      {attrs.parseError ? (
        <Alert.Root status="warning" mt={3} borderRadius="md">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Invalid files config</Alert.Title>
            <Alert.Description>{attrs.parseError}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      <Box mt={3}>
        <Suspense
          fallback={
            <Text color="fg.muted" fontSize="sm">
              Loading uploads…
            </Text>
          }
        >
          <DocumentFilesBlock
            documentId={context.documentId}
            apiHost={context.apiHost}
            userEmail={context.userEmail}
            documentPath={context.parentPath}
          />
        </Suspense>
      </Box>
    </NodeViewWrapper>
  );
}

export const MarkdownFilesNode = Node.create<MarkdownFilesNodeOptions>({
  name: 'npFiles',
  group: 'block',
  atom: true,
  selectable: true,
  isolating: true,
  defining: true,

  addOptions() {
    return {
      contextRef: { current: null as never },
    };
  },

  addAttributes() {
    return {
      data: {
        default: normalizeFilesBlockData({}),
      },
      rawConfig: {
        default: '',
      },
      parseError: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: NP_FILES_TAG,
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false;

          const parsed = parseMarkdownFilesConfig(element.textContent ?? '');
          return {
            data: parsed.data,
            rawConfig: parsed.rawConfig,
            parseError: parsed.parseError,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = normalizeFilesAttrs(HTMLAttributes);
    return [NP_FILES_TAG, serializeMarkdownFilesInnerConfig(attrs)];
  },

  renderMarkdown(node) {
    const attrs = normalizeFilesAttrs(node?.attrs);
    return serializeMarkdownFilesBlock(attrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MarkdownFilesNodeView);
  },
});
